/**
 * Utility functions for formatting and parsing time durations
 */

/**
 * Formats seconds into a human-readable duration like "2h 30m" or "45m"
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
export function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return ''

  if (seconds < 60) return '< 1m'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (hours === 0) return `${minutes}m`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

/**
 * Formats seconds into clock format "HH:MM:SS" for running timers
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted time string like "00:05:32"
 */
export function formatElapsed(seconds) {
  if (!seconds || seconds < 0) seconds = 0

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  const pad = (n) => n.toString().padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`
}

/**
 * Parses various duration input formats into seconds
 * Supported formats:
 * - "2:30" or "2:30:00" -> hours:minutes or hours:minutes:seconds
 * - "2h 30m" or "2h30m" -> hours and minutes
 * - "90m" or "90" -> minutes only
 * - "1.5h" -> decimal hours
 * @param {string} input - User input string
 * @returns {number|null} Duration in seconds, or null if invalid
 */
export function parseDurationInput(input) {
  if (!input || typeof input !== 'string') return null

  const trimmed = input.trim().toLowerCase()
  if (!trimmed) return null

  // Try "HH:MM" or "HH:MM:SS" format
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/)
  if (colonMatch) {
    const hours = parseInt(colonMatch[1], 10)
    const minutes = parseInt(colonMatch[2], 10)
    const seconds = colonMatch[3] ? parseInt(colonMatch[3], 10) : 0
    if (minutes < 60 && seconds < 60) {
      return hours * 3600 + minutes * 60 + seconds
    }
  }

  // Try "Xh Ym" format (with or without spaces)
  const hhmm = trimmed.match(/^(\d+(?:\.\d+)?)\s*h(?:\s*(\d+)\s*m)?$/)
  if (hhmm) {
    const hours = parseFloat(hhmm[1])
    const minutes = hhmm[2] ? parseInt(hhmm[2], 10) : 0
    return Math.round(hours * 3600 + minutes * 60)
  }

  // Try "Xm" format
  const minOnly = trimmed.match(/^(\d+)\s*m$/)
  if (minOnly) {
    return parseInt(minOnly[1], 10) * 60
  }

  // Try plain number (treat as minutes)
  const plainNum = trimmed.match(/^(\d+)$/)
  if (plainNum) {
    return parseInt(plainNum[1], 10) * 60
  }

  return null
}

/**
 * Formats seconds into "H:MM" input format for editing
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted string like "2:30"
 */
export function formatDurationInput(seconds) {
  if (!seconds || seconds <= 0) return ''

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  return `${hours}:${minutes.toString().padStart(2, '0')}`
}
